<script setup lang="ts">
/**
 * A placeholder shape shown while content loads — it keeps the layout from
 * jumping when the data arrives. `text` draws `lines` lines of body text (the
 * last one shorter), `line` a single bar, `circle` an avatar-like dot, `rect`
 * a block (a card, an image).
 *
 * Purely decorative (`aria-hidden`): the region that loads carries
 * `aria-busy`, or a labelled NxSpinner announces it. The shimmer stops under
 * `prefers-reduced-motion`.
 */
import { computed, type CSSProperties } from 'vue';

export type NxSkeletonVariant = 'text' | 'line' | 'circle' | 'rect';

const props = withDefaults(
  defineProps<{
    /** Shape. Defaults to `text`. */
    variant?: NxSkeletonVariant;
    /** Number of lines for `text`. Defaults to 3. */
    lines?: number;
    /** CSS width (`'60%'`, `'12em'`, a token `var(...)`); defaults to the full width (circle: its height). */
    width?: string;
    /** CSS height for `rect` and `circle`; lines take the text height. */
    height?: string;
  }>(),
  { variant: 'text', lines: 3, width: undefined, height: undefined }
);

const lineCount = computed(() =>
  props.variant === 'text' ? Math.max(1, Math.floor(props.lines)) : 1
);

const rootStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {};
  if (props.width) style.width = props.width;
  if (props.height && (props.variant === 'rect' || props.variant === 'circle')) {
    style.height = props.height;
    if (props.variant === 'circle' && !props.width) style.width = props.height;
  }
  return style;
});
</script>

<template>
  <span
    class="nx-skeleton"
    :class="`variant-${props.variant}`"
    :style="rootStyle"
    aria-hidden="true"
    data-testid="nx-skeleton"
  >
    <template v-if="props.variant === 'text'">
      <span v-for="index in lineCount" :key="index" class="nx-skeleton__bone nx-skeleton__line" />
    </template>
    <span v-else class="nx-skeleton__bone" />
  </span>
</template>

<style scoped>
.nx-skeleton {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  width: 100%;
  max-width: 100%;
}
.nx-skeleton__bone {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: var(--nxt-radius-sm);
  background-color: var(--nxt-surface-sunken-bg);
  background-image: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in srgb, var(--nxt-border) 60%, transparent) 50%,
    transparent 100%
  );
  background-repeat: no-repeat;
  background-size: 200% 100%;
  animation: nx-skeleton-shimmer calc(var(--nxt-motion-slow) * 5) ease-in-out infinite;
}
.nx-skeleton__line {
  height: 1em;
}
.variant-text .nx-skeleton__line:last-child:not(:first-child) {
  width: 60%;
}
.variant-line .nx-skeleton__bone {
  height: 1em;
}
.variant-rect {
  height: var(--nxt-area-size-sm);
}
.variant-rect .nx-skeleton__bone {
  border-radius: var(--nxt-radius-md);
}
.variant-circle {
  width: var(--nxt-avatar-size-md);
  height: var(--nxt-avatar-size-md);
  flex: 0 0 auto;
}
.variant-circle .nx-skeleton__bone {
  border-radius: var(--nxt-radius-full);
}
@keyframes nx-skeleton-shimmer {
  from {
    background-position: 150% 0;
  }
  to {
    background-position: -50% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .nx-skeleton__bone {
    animation: none;
    background-image: none;
  }
}
/* The same where a subtree asks for less motion: data-nx-motion="reduced"
   on an ancestor (reset.css does the durations). */
:where([data-nx-motion='reduced']) .nx-skeleton__bone {
  animation: none;
  background-image: none;
}
</style>
